import { Button, IconButton } from "@hortis/ui/button";
import { DialogTrigger } from "@hortis/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { FormControl, FormField } from "@hortis/ui/form";
import {
  BezierCurve2,
  CoinsStacked3,
  DotsHorizontal,
  Grid3,
  IntersectCircle,
  MarkerPin2,
  MarkerPin4,
  Scales1,
  TagNumber,
} from "@hortis/ui/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { MaterialGroup, PlantMaterialStatus } from "generated/graphql";
import { Fragment, useState } from "react";
import { useFormContext } from "react-hook-form";
import { SexPicker, SexPickerLabel } from "src/components/pickers/sex-picker";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { CreateObservationDialog } from "src/features/material-page/create-observation-dialog/create-observation-dialog";
import { ObservationConditionRating } from "src/features/material-page/sections/material-record-observations/observation-condition";
import { observationConditionLabelMap } from "src/utils/label-maps/observation-condition";
import { twMerge } from "tailwind-merge";
import { match } from "ts-pattern";
import { MapPositionDialog } from "../add-plant-material-form/map-position-field/map-position-dialog";
import type { MaterialSchemaInput } from "../add-plant-material-form/schema";
import {
  QuantityWeightDialog,
  type QuantityWeightTab,
} from "./quantity-weight-dialog";
import { TagNumberDialog } from "./tag-number-dialog";

export const MaterialFieldsMoreDropdown = ({
  siteSlug,
}: { siteSlug?: string }) => {
  const form = useFormContext<Partial<MaterialSchemaInput>>();
  const [observationDialogOpen, setObservationDialogOpen] = useState(false);
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [quantityWeightDialogOpen, setQuantityWeightDialogOpen] =
    useState(false);
  const [tagNumberDialogOpen, setTagNumberDialogOpen] = useState(false);
  const [initialQuantityTab, setInitialQuantityTab] =
    useState<QuantityWeightTab>("quantity");
  const tagNumber = form.watch("tagNumber");
  const position = form.watch("position");
  const observation = form.watch("initialObservation");
  const followUp = form.watch("followUp");
  const quantity = form.watch("quantity");
  const weight = form.watch("weight");
  const massPlanting = form.watch("massPlanting");
  const sex = form.watch("sex");
  const materialGroup = form.watch("materialGroup");
  const status = form.watch("status");

  const showTagNumberItem = tagNumber == null;
  const showSexItem = sex == null;
  const showPositionItem = position == null;
  const observationApplicable =
    materialGroup === MaterialGroup.Plant &&
    status != PlantMaterialStatus.Absent;
  const showObservationItem = observation == null && observationApplicable;

  const [sexPickerOpen, setSexPickerOpen] = useState(false);

  const massPlantingEnabled = massPlanting === true;
  const quantityEnabled = quantity != null;
  const weightEnabled = weight != null;
  const showQuantityWeightItem =
    !massPlantingEnabled && !quantityEnabled && !weightEnabled;
  const quantityWeightTab: QuantityWeightTab = quantityEnabled
    ? "quantity"
    : weightEnabled
      ? "weight"
      : "massPlanting";

  const menuItems = [
    {
      visible: showTagNumberItem,
      content: (
        <DropdownMenuItem
          onSelect={() => {
            setTagNumberDialogOpen(true);
          }}
          data-cy="tag-number-menu-item"
        >
          <TagNumber className="h-4 w-4" />
          Set tag number
        </DropdownMenuItem>
      ),
    },
    {
      visible: showPositionItem,
      content: (
        <DropdownMenuItem
          onSelect={() => {
            setPositionDialogOpen(true);
          }}
          data-cy="position-menu-item"
        >
          <MarkerPin2 className="h-4 w-4" />
          Set position
        </DropdownMenuItem>
      ),
    },
    {
      visible: showObservationItem,
      content: (
        <DropdownMenuItem
          onSelect={() => {
            setObservationDialogOpen(true);
          }}
          data-cy="observation-menu-item"
        >
          <Grid3 className="h-4 w-4" />
          Add initial observation
        </DropdownMenuItem>
      ),
    },
    {
      visible: showSexItem,
      content: (
        <DropdownMenuItem
          onSelect={() => {
            setSexPickerOpen(true);
          }}
          data-cy="sex-menu-item"
        >
          <IntersectCircle className="h-4 w-4" />
          Set sex
        </DropdownMenuItem>
      ),
    },
    {
      visible: showQuantityWeightItem,
      content: (
        <DropdownMenuGroup>
          <DropdownMenuItem
            onSelect={() => {
              setQuantityWeightDialogOpen(true);
              setInitialQuantityTab("quantity");
            }}
            data-cy="quantity-menu-item"
          >
            <CoinsStacked3 className="h-4 w-4" />
            Set quantity
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              setQuantityWeightDialogOpen(true);
              setInitialQuantityTab("weight");
            }}
            data-cy="weight-menu-item"
          >
            <Scales1 className="h-4 w-4" />
            Set weight
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              form.setValue("massPlanting", true);
            }}
            data-cy="mass-planting-menu-item"
          >
            <BezierCurve2 className="h-4 w-4" />
            Set mass planting
          </DropdownMenuItem>
        </DropdownMenuGroup>
      ),
    },
  ];

  return (
    <>
      <Tooltip>
        <FormField
          control={form.control}
          name="sex"
          render={({ field }) => (
            <FormControl>
              <Tooltip>
                <SexPicker
                  open={sexPickerOpen}
                  onOpenChange={setSexPickerOpen}
                  placeholder={
                    field.value == null ? "Set sex..." : "Change sex..."
                  }
                  mode={showSexItem ? "dialog" : undefined}
                  {...field}
                >
                  {showSexItem ? null : (
                    <TooltipTrigger asChild>
                      <Button
                        size="xs"
                        startIcon={IntersectCircle}
                        className={twMerge(
                          field.value == null && "text-grey-500",
                        )}
                      >
                        {field.value == null ? (
                          "Sex"
                        ) : (
                          <SexPickerLabel value={field.value} />
                        )}
                      </Button>
                    </TooltipTrigger>
                  )}
                </SexPicker>
                <TooltipContent side="bottom" sideOffset={0}>
                  {field.value == null ? "Set sex" : "Change sex"}
                </TooltipContent>
              </Tooltip>
            </FormControl>
          )}
        />
        <QuantityWeightDialog
          open={quantityWeightDialogOpen}
          onOpenChange={setQuantityWeightDialogOpen}
          onSubmit={(data) => {
            form.setValue("quantity", data.quantity);
            form.setValue("weight", data.weight);
            form.setValue("massPlanting", data.massPlanting);
          }}
          initialValue={{
            quantity: quantity,
            weight: weight,
            massPlanting: massPlanting,
          }}
          initialTab={
            showQuantityWeightItem ? initialQuantityTab : quantityWeightTab
          }
        >
          {!showQuantityWeightItem && (
            <TooltipTrigger asChild>
              <Button
                size="xs"
                startIcon={match(quantityWeightTab)
                  .with("weight", () => Scales1)
                  .with("quantity", () => CoinsStacked3)
                  .with("massPlanting", () => BezierCurve2)
                  .exhaustive()}
                testId="quantity-weight-button"
              >
                {match(quantityWeightTab)
                  .with("quantity", () => quantity)
                  .with("weight", () => `${weight ?? ""} grams`)
                  .with("massPlanting", () => "Mass planting")
                  .exhaustive()}
              </Button>
            </TooltipTrigger>
          )}
        </QuantityWeightDialog>
        <TooltipContent side="bottom" sideOffset={0}>
          Set{" "}
          {match(quantityWeightTab)
            .with("quantity", () => "quantity")
            .with("weight", () => "weight")
            .with("massPlanting", () => "mass planting")
            .exhaustive()}
        </TooltipContent>
      </Tooltip>
      <CreateObservationDialog
        open={observationDialogOpen}
        onOpenChange={setObservationDialogOpen}
        showRemove={true}
        siteSlug={siteSlug}
        initialValues={
          observation == null ? undefined : { ...observation, followUp }
        }
        onSubmit={(data) => {
          if (data == null) {
            form.setValue("initialObservation", null, { shouldDirty: true });
            form.setValue("followUp", null, { shouldDirty: true });
            return;
          }
          const { followUp, ...initialObservation } = data;
          form.setValue("initialObservation", initialObservation, {
            shouldDirty: true,
          });
          form.setValue("followUp", followUp, { shouldDirty: true });
        }}
      >
        {observation != null && observationApplicable && (
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
                <Button
                  size="xs"
                  testId="observation-button"
                  onClick={() => {
                    setObservationDialogOpen(true);
                  }}
                >
                  <ObservationConditionRating
                    condition={observation.condition}
                  />
                  {observationConditionLabelMap[observation.condition]}
                </Button>
              </DialogTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={0}>
              Change initial observation
            </TooltipContent>
          </Tooltip>
        )}
      </CreateObservationDialog>
      <FormField
        control={form.control}
        name="position"
        render={({ field }) => (
          <Tooltip>
            <MapPositionDialog
              value={field.value}
              onChange={field.onChange}
              open={positionDialogOpen}
              onOpenChange={setPositionDialogOpen}
              siteSlug={siteSlug}
            >
              {position == null ? (
                <></>
              ) : (
                <TooltipTrigger asChild>
                  <Button
                    size="xs"
                    startIcon={MarkerPin4}
                    testId="position-button"
                  >
                    {Number(position.latitude).toFixed(3)},{" "}
                    {Number(position.longitude).toFixed(3)}
                  </Button>
                </TooltipTrigger>
              )}
            </MapPositionDialog>
            <TooltipContent side="bottom" sideOffset={0}>
              Change position
            </TooltipContent>
          </Tooltip>
        )}
      />
      <FormField
        control={form.control}
        name="tagNumber"
        render={({ field }) => (
          <Tooltip>
            <TagNumberDialog
              initialValue={field.value}
              open={tagNumberDialogOpen}
              onOpenChange={setTagNumberDialogOpen}
              onSubmit={field.onChange}
            >
              {tagNumber == null ? (
                <></>
              ) : (
                <TooltipTrigger asChild>
                  <Button
                    size="xs"
                    startIcon={TagNumber}
                    className={twMerge(
                      tagNumber === "" ? "text-grey-500" : undefined,
                    )}
                    testId="tag-number-button"
                  >
                    {tagNumber === "" ? "Tag number" : tagNumber}
                  </Button>
                </TooltipTrigger>
              )}
            </TagNumberDialog>
            <TooltipContent side="bottom" sideOffset={0}>
              Set tag number
            </TooltipContent>
          </Tooltip>
        )}
      />
      <DropdownMenu>
        {menuItems.some((item) => item.visible) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <IconButton
                  icon={DotsHorizontal}
                  ariaLabel="More fields"
                  size="xs"
                  className="text-grey-500"
                  testId="more-material-fields-button"
                  onFocusCapture={stopEventPropagation}
                />
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={0}>
              More actions
            </TooltipContent>
          </Tooltip>
        )}
        <DropdownMenuContent>
          {menuItems
            .filter((item) => item.visible)
            .map((item, index, filteredItems) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {item.content}
                {index < filteredItems.length - 1 && <DropdownMenuSeparator />}
              </Fragment>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
